// src/pages/AuditLog/AuditLogList.jsx
// -----------------------------------------------------------------------------
// Admin-only, read-only view of the audit trail (Additional Requirement:
// immutable audit trail of who performed each action and when). Rows are
// written automatically by the API on every create / update / delete / login.
// -----------------------------------------------------------------------------
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import axiosClient from '../../api/axiosClient';
import ENDPOINTS from '../../api/endpoints';
import userService from '../../api/userService';
import Loader from '../../components/common/Loader';

const ACTIONS = ['', 'CREATE', 'UPDATE', 'DELETE', 'LOGIN'];

// Turns {"Status_ID":3} JSON into "Status_ID: 3" lines for display
const formatValues = (json) => {
  if (!json) return '';
  try {
    return Object.entries(JSON.parse(json))
      .map(([k, v]) => `${k}: ${v === null ? '—' : typeof v === 'object' ? JSON.stringify(v) : v}`)
      .join('\n');
  } catch {
    return json;
  }
};

export default function AuditLogList() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ entityName: '', actionType: '', userId: '', from: '', to: '' });

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries({ ...filters, take: 500 }).filter(([, v]) => v !== ''));
      const res = await axiosClient.get(ENDPOINTS.AUDIT_LOGS, { params });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load audit log.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    userService.getAll().then((res) => setUsers(res.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userName = (id) => {
    if (!id) return 'System';
    const u = users.find((x) => Number(x.id ?? x.userID) === Number(id));
    return u ? `${u.first_Name || ''} ${u.last_Name || ''}`.trim() || u.email : `User #${id}`;
  };

  const entityNames = [...new Set(rows.map((r) => r.entityName))].sort();
  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Audit Log</h1>
          <div className="subtitle">Who changed what, and when. Entries cannot be edited or deleted.</div>
        </div>
      </div>

      <form
        className="table-toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          load();
        }}
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}
      >
        <input
          list="audit-entities"
          placeholder="Table (e.g. Ticket_TBL)"
          value={filters.entityName}
          onChange={setFilter('entityName')}
        />
        <datalist id="audit-entities">
          {entityNames.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>
        <select value={filters.actionType} onChange={setFilter('actionType')}>
          {ACTIONS.map((a) => (
            <option key={a} value={a}>
              {a || 'All actions'}
            </option>
          ))}
        </select>
        <select value={filters.userId} onChange={setFilter('userId')}>
          <option value="">All users</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {`${u.first_Name || ''} ${u.last_Name || ''}`.trim() || u.email}
            </option>
          ))}
        </select>
        <input type="date" value={filters.from} onChange={setFilter('from')} title="From" />
        <input type="date" value={filters.to} onChange={setFilter('to')} title="To" />
        <button type="submit" className="btn btn-primary">
          Search
        </button>
      </form>

      {loading ? (
        <Loader label="Loading audit log..." />
      ) : rows.length === 0 ? (
        <p className="text-muted">No audit entries found.</p>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>When</th>
                <th>User</th>
                <th>Action</th>
                <th>Table</th>
                <th>ID</th>
                <th>Before</th>
                <th>After</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.auditId}>
                  <td style={{ whiteSpace: 'nowrap' }}>{new Date(r.actionDttm).toLocaleString()}</td>
                  <td>{userName(r.userId)}</td>
                  <td>{r.actionType}</td>
                  <td>{r.entityName}</td>
                  <td>{r.entityId}</td>
                  <td style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{formatValues(r.oldValue)}</td>
                  <td style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{formatValues(r.newValue)}</td>
                  <td>{r.ipAddress || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
